"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LeftArrowIcon } from "staysure-component-library";

import BtnLoadingSvg from "@/components/atoms/btn-loading-svg/btn-loading-svg";
import LabelButtonWrapper, {
  IconButtonWrapper,
} from "@/components/atoms/button-wrapper/button-wrapper";
import MedicalContentSection from "@/components/molecules/medical-section/medical-content-section";
import ConfirmationsModal from "@/components/molecules/quote-section/edit-quote-section/confirmation-modal";
import {
  Individual,
  MedicalEdit,
  MedicalLandingPage,
  OrganizerEdit,
  Quote,
  QuoteEdit,
  TravellerEdit,
} from "@/config/app-constant";
import useMedicalNavigation from "./hooks/use-medical-navigation";
import useMedicalTerms from "@/hooks/api/medical/use-medical-terms";
import useFetchQuoteData from "@/hooks/api/quote/use-fetch-quote";
import useGTMMedicalEligibilityAgreement from "@/hooks/gtm/use-gtm-medical-eligibility-agreement";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import useReplaceRouterPath from "@/hooks/use-replace-router-path/use-replace-router-path";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { setQuote } from "@/store/slices/quote/quote.slice";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { getRouterPathMainButton } from "@/utils/page-routing.utils";
import { MedicalLandingContentType } from "@/utils/schema/traveler-health-scema.utils";

import Styles from "./medicalSection.module.css";
import editQuoteStyle from "../../molecules/quote-section/edit-quote-section/edit-quote.module.css";

type MedicalLandingProps = {
  medicalLandingContent: MedicalLandingContentType | null;
};

function MedicalSection({
  medicalLandingContent,
}: Readonly<MedicalLandingProps>) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { hideLoading } = useLoading();
  const [modalOpen, setModalOpen] = useState(false);

  const { attributes, version, referenceId } = useSelector(
    (state: RootState) => state.quote
  );

  const isdesktop = useMediaQuery("(min-width: 600px)");
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const editPage = searchParams.get(MedicalEdit);
  const organizerEdit = searchParams.get(OrganizerEdit);
  const editTraveller = searchParams.get(TravellerEdit);

  const affiliate = searchParams.get("affiliate");
  const quoteRef = searchParams.get("quoteId");

  const { data, error, loading } = useFetchQuoteData(
    referenceId,
    quoteRef,
    affiliate
  );

  const { resetQuestionData } = useResetStateTravelerEdit();
  const pathname = usePathname();
  useReplaceRouterPath(pathname);
  const { updateMedicalEligibilityAgreement } =
    useGTMMedicalEligibilityAgreement();

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading) hideLoading();
      if (!loading && data) {
        dispatch(setQuote(data));
      }
      if (!loading && error !== null) {
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          dispatch(setApiError(await apiErrorMappings(error)));
        }
      }
    };
    handleEffect();
  }, [loading, data, error, router, dispatch, hideLoading]);

  const { patchMedicalTerms, medData, medError, medLoading } =
    useMedicalTerms();

  const { handleBackButtonClick } = useMedicalNavigation();

  useEffect(() => {
    const eventHandler = (e: BeforeUnloadEvent) => {
      const confirmationMessage =
        "It looks like you have been editing something. " +
        "If you leave before saving, your changes will be lost.";

      if (e) {
        e.preventDefault();
        sessionStorage.removeItem("organiserTravellerId");
        (e || window.event).returnValue = confirmationMessage;
      }

      return confirmationMessage;
    };

    const isOnIOS =
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPhone/i);
    const eventName = isOnIOS ? "pagehide" : "beforeunload";

    if (editPage || organizerEdit || editTraveller) {
      window.addEventListener(eventName, eventHandler);
    }

    return () => {
      window.removeEventListener(eventName, eventHandler);
    };
  }, [editPage, organizerEdit, editTraveller]);

  useEffect(() => {
    const handleEffect = async () => {
      if (!medLoading && medData) {
        let path = getRouterPathMainButton(MedicalLandingPage);
        if (editPage) {
          path = `${path}/?${MedicalEdit}=true`;
        } else if (editTraveller) {
          path = `${path}/?${TravellerEdit}=true`;
        } else if (organizerEdit) {
          path = `${path}/?${OrganizerEdit}=true`;
        }
        router.push(path);
        setButtonLoading(false);
      }
      if (!medLoading && medError !== null) {
        if (medError.response?.status === 401) {
          router.push("/");
        } else {
          dispatch(setApiError(await apiErrorMappings(medError)));
          setButtonLoading(false);
        }
      }
    };
    handleEffect();
  }, [
    dispatch,
    editPage,
    editTraveller,
    medData,
    medError,
    medLoading,
    router,
    organizerEdit,
  ]);

  const handleMainButtonClick = async () => {
    setButtonLoading(true);

    // GTM tracking
    updateMedicalEligibilityAgreement(
      "quote_journey",
      "medical_eligibility_check"
    );

    await patchMedicalTerms({
      referenceId,
      version,
    });
    if (referenceId !== "") {
      setButtonLoading(true);
      await patchMedicalTerms({
        referenceId,
        version,
      });
    }
  };

  if (!medicalLandingContent) {
    return null;
  }
  const {
    titleRiskLevel,
    supportingText,
    travelEligibilityContent,
    ctaButtonText,
    cancelEditingBtnText,
  } = medicalLandingContent;

  const renderMedicalContentSection = () => {
    return (
      <MedicalContentSection
        titleRiskLevel={titleRiskLevel}
        supportingText={supportingText}
        travelEligibilityContent={travelEligibilityContent}
        linkText=""
        isAvatar={false}
      />
    );
  };

  const renderBackButton = () => {
    return (
      <IconButtonWrapper
        data-testid="traveller-health-back-button"
        id="traveller-health-back-button"
        btnType="icon"
        onClick={handleBackButtonClick}
        semanticColor={isdesktop ? "Dark Background" : "Light Background"}
        icon={<LeftArrowIcon />}
        hierarchy="secondary"
        className={`z-[2] relative 
       min-w-[56px] w-[56px] min-h-[56px] h-[56px] flex justify-center 
       items-center leading-[18px] focus:!border-icon-on-dark`}
      />
    );
  };

  const handleCancelEdit = () => {
    setModalOpen(true);
  };

  const renderCancelBtn = () => {
    return (
      <div>
        <LabelButtonWrapper
          hierarchy="tertiary"
          label={cancelEditingBtnText || "Cancel editing"}
          id="cancel-edit-medical"
          semanticColor="Dark Background"
          className={`${editQuoteStyle.cancelEditButton}`}
          onClick={handleCancelEdit}
        />
        <ConfirmationsModal
          isOpen={modalOpen}
          handleConfirmBtnClick={() => {
            router.push(`/${Quote}/?${QuoteEdit}=true`);
            resetQuestionData();
          }}
          handleCancelmBtnClick={() => {
            setModalOpen(false);
          }}
        />
      </div>
    );
  };

  return (
    <main>
      <div
        className={` ${Styles.grid_block} grid md:grid-cols-12 grid-cols-6 gap-spacing-l xl:mx-auto lg:mx-[72px] md:mx-[48px] mx-0 !h-full relative lg:block md:flex flex-col`}
      >
        <div className={`${Styles.question_layout_wrapper} col-span-12`}>
          <div
            className={` ${Styles.question_layout} bg-dark-surface-accent-1-primary min-h-full text-surface-neutral-option-2 rounded-radius-xxl relative`}
          >
            <div
              className={` ${Styles.content_box}  md:pt-spacing-xl md:pb-spacing-xl sm:pb-spacing-m sm:pt-0 px-spacing-m flex flex-col items-center`}
            >
              {(editPage || editTraveller || organizerEdit) &&
                renderCancelBtn()}
              {renderMedicalContentSection()}
            </div>
            <div className="flex justify-center w-full px-6 py-3 pt-0 md:relative fixed z-[1] bottom-0 left-0 md:bg-transparent bg-interactive-overlay-image md:shadow-none gap-x-[16px]">
              <div className="flex justify-center w-full px-6 py-3 md:pt-0 md:relative fixed z-[1] bottom-0 left-0 md:bg-transparent bg-interactive-overlay-image md:shadow-none gap-x-[16px]">
                {!editPage && renderBackButton()}

                <LabelButtonWrapper
                  id="traveller-health-submit-button"
                  data-testid="traveller-health-submit-button"
                  isDisabled={false}
                  label={ctaButtonText}
                  hierarchy="primary"
                  className={`${
                    !editPage
                      ? "lg:w-[342px] md:w-[342px] sm:w-[244px]"
                      : "lg:!w-[414px] md:!w-[400px] sm:!w-[312px]"
                  } flex flex-col text-text-on-primary  justify-center items-center gap-grid-system-space-050 z-[2] relative px-spacing-l py-spacing-s font-body-font text-xs leading-[18px] font-semibold h-[56px]`}
                  type="button"
                  isLoading={buttonLoading}
                  onClick={handleMainButtonClick}
                  loadingIcon={buttonLoading ? <BtnLoadingSvg /> : <div />}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default MedicalSection;
